import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntil } from 'rxjs/operators';
import { HxOrganizationService, HxUiLabelPipe, OrganizationModel, PagedList, toRouterQueryParams, uiLabel } from 'hx-services';
import { HxPagedListDirective } from 'hx-components';
import { PaymentAgreementEditComponent } from '@adm-web-app/modules/organizations/payment-agreement-edit/payment-agreement-edit.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NgFor } from '@angular/common';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import Timeout = NodeJS.Timeout;

interface ListFilter {
  name: string;
  options: { label: string, value: string | number, checked?: boolean }[];
  isVisible: boolean;
  isActive: boolean;
  operator: 'and' | 'or';
}

interface OrganizationItem extends OrganizationModel {
  uin: string;
}

@Component({
  selector: 'hc-organization-list',
  templateUrl: './organization-list.component.html',
  styleUrls: ['./organization-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, NzInputDirective, FormsModule, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzWaveDirective, NzTooltipDirective, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class OrganizationListComponent extends HxPagedListDirective<OrganizationItem> implements OnInit {
  filter: {
    query?: string | null;
  } = {};
  private timeoutId?: Timeout;

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    private orgService: HxOrganizationService,
    private modalService: NzModalService,
    private tr: TranslocoService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        this.filter.query = paramMap.get('query');
        super.load(paramMap);
      });
  }

  resetFilter(filter: ListFilter) {
    filter.options.forEach(op => op.checked = false);
  }

  applyFilters(delay?: number) {
    if (delay) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => this.navigate(), delay);
    } else {
      this.navigate();
    }
  }

  protected loadList(): Promise<PagedList<OrganizationItem>> {
    return this.orgService.getOrganizations({
      page: this.page,
      includeDisabled: true,
      limit: this.limit,
      searchText: this.filter.query ?? undefined,
      sort: this.getSortParams(),
    }).then(res => ({list: res.list.map(org => ({...org.organization, ...{uin: org.uin}})), count: res.count}));
  }

  remove(id: number): void {
    this.orgService.removeOrganization(id).then(() => this.loadData());
  }

  showPaymentAgreementModal(org: OrganizationModel) {
    const modalRef = this.modalService.create({
      nzTitle: this.tr.translate('organizations.paymentAgreement.title') + ': ' + uiLabel(this.tr.getActiveLang(), org.title),
      nzContent: PaymentAgreementEditComponent,
      nzFooter: null,
      nzWidth: 'inherit',
      nzWrapClassName: 'hx-modal-xlg-wrap'
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.organization = JSON.parse(JSON.stringify(org)) as OrganizationModel;
    }
  }

  private navigate() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: toRouterQueryParams(this.filter),
    });
  }
}
